'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './IntroVideo.module.scss';

export default function IntroVideo({ src, poster, label, duration, title, closeLabel, errorLabel, fallbackLabel }) {
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const videoRef = useRef(null);
  const dialogRef = useRef(null);
  const triggerRef = useRef(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    const video = videoRef.current;
    const overflow = document.body.style.overflow;
    // A native modal keeps focus inside and makes the underlying page inert.
    dialog.showModal();
    document.body.style.overflow = 'hidden';
    // If a browser restricts autoplay, its native play control remains available.
    if (video.getAttribute('src') !== src) video.src = src;
    video.play().catch(() => {});
    return () => {
      video.pause();
      video.removeAttribute('src');
      video.load();
      dialog.close();
      document.body.style.overflow = overflow;
      triggerRef.current?.focus({ preventScroll: true });
    };
  }, [open, src]);

  return <>
    <button ref={triggerRef} type="button" className={styles.trigger}
      aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined}
      onClick={() => { setFailed(false); setOpen(true); }}>
      <span className={styles.play} aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" focusable="false"><path d="M8 5v14l11-7z"/></svg>
      </span>
      <span>{label}</span>
      <span aria-hidden="true">·</span>
      <span className={styles.duration}>{duration}</span>
    </button>
    {open && createPortal(
      <dialog ref={dialogRef} id={id} className={styles.dialog} aria-labelledby={id + '-title'}
        onCancel={event => { event.preventDefault(); setOpen(false); }}
        onClick={event => { if (event.target === event.currentTarget) setOpen(false); }}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h2 id={id + '-title'}>{title}</h2>
            <button type="button" className={styles.close} aria-label={closeLabel} autoFocus onClick={() => setOpen(false)}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false"><path d="m6 6 12 12M18 6 6 18"/></svg>
            </button>
          </div>
          <div className={styles.player}>
            {/* Neither the video nor its poster is requested before the modal opens. */}
            <video ref={videoRef} src={src} poster={poster} controls autoPlay playsInline
              preload="metadata" aria-label={title} onError={() => setFailed(true)}/>
          </div>
          {failed && <p className={styles.error} role="alert">
            {errorLabel} <a href={src} target="_blank" rel="noopener noreferrer">{fallbackLabel}</a>
          </p>}
        </div>
      </dialog>,
      document.body
    )}
  </>;
}
